
import { useState } from 'react';
import { Link } from 'react-router-dom';

const ShopPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const categories = [
    {
      id: 'weapons',
      title: 'Weapons & Ammunition',
      description: 'Browse our selection of firearms and ammunition with pricing calculator',
      icon: 'ri-sword-line',
      link: '/shop/weapons'
    },
    {
      id: 'tactical-gear',
      title: 'Tactical Gear',
      description: 'Tactical equipment, gear, and accessories with specifications',
      icon: 'ri-calculator-line',
      link: '/tactical-gear'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg" style={{ fontFamily: '"Pacifico", serif' }}>TA</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>Tactical Arms</h1>
                <p className="text-xs text-orange-400">NXT Cam Properties</p>
              </div>
            </Link>
            
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-300 hover:text-orange-400 transition-colors">Home</Link>
              <Link to="/official-discounts" className="text-gray-300 hover:text-orange-400 transition-colors">Official Discounts</Link>
              <Link to="/shop" className="text-orange-400 font-medium">Shop</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              <i className="ri-store-3-line text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Tactical Arms Shop</h1>
              <p className="text-xl text-orange-400">Weapons & Tactical Equipment Pricing Reference</p>
            </div>
          </div>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Browse our comprehensive catalog of firearms, ammunition, and tactical gear. 
            Use our pricing calculators and specification tools to find exactly what you need.
          </p>
        </div>

        {/* Category Selection */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`relative group cursor-pointer transition-all duration-300 ${
                selectedCategory === category.id ? 'scale-105' : 'hover:scale-102'
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 h-full">
                <div className="flex flex-col items-center text-center space-y-6">
                  {/* Icon */}
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <i className={`${category.icon} text-3xl text-white`}></i>
                  </div>
                  
                  {/* Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">{category.title}</h3>
                    <p className="text-gray-300 text-base leading-relaxed mb-6">
                      {category.description}
                    </p>
                  </div>
                  
                  {/* Action Button */}
                  <Link
                    to={category.link}
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 whitespace-nowrap group-hover:scale-105"
                  >
                    <span>Browse {category.title}</span>
                    <i className="ri-arrow-right-line text-lg"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-orange-500/20">
            <h3 className="text-2xl font-bold text-white mb-4">Professional Pricing Reference</h3>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Our pricing tools provide accurate market references for firearms, ammunition, and tactical equipment. 
              All prices are based on current market conditions and typical retail values.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <i className="ri-shield-check-line text-orange-400"></i>
                <span>Licensed Dealer</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-price-tag-3-line text-orange-400"></i>
                <span>Market Pricing</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-calculator-line text-orange-400"></i>
                <span>Pricing Tools</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/80 border-t border-orange-500/20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400">© 2024 Tactical Arms - NXT Cam Properties</p>
              <p className="text-gray-500 text-sm">Professional Firearms & Tactical Equipment</p>
            </div>
            <div className="flex items-center space-x-6">
              <a href="https://readdy.ai/?origin=logo" className="text-gray-500 hover:text-orange-400 transition-colors text-sm">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShopPage;
